import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationInfoService } from '@app/core/guards/authentication-info.service';
import { AttachmentType } from '@app/core/models/attachment-type.model';
import { AttachmentModel } from '@app/core/models/attachment.model';
import { InvoiceActionType } from '@app/core/models/invoices/invoice-action-type.model';
import { InvoiceDetailModel } from '@app/core/models/invoices/invoice-detail.model';
import { InvoiceViewType } from '@app/core/models/invoices/invoice-view-type.model';
import { InvoiceService } from '@app/core/services/invoice.service';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-invoice-pdf-viewer',
  templateUrl: './invoice-pdf-viewer.component.html',
  styleUrls: ['./invoice-pdf-viewer.component.scss'],
})
export class InvoicePdfViewerComponent implements OnInit, OnDestroy {
  private unsubscribe$: Subject<boolean> = new Subject<boolean>();

  invoice: InvoiceDetailModel;
  invoiceViewType: string;
  attachments: Array<AttachmentModel>;

  canDeleteAttachment: boolean = false;
  CanManageInvoiceAGI: boolean = false;
  isWaitingInvoice: boolean = false;

  hidePDFViewerButtonForPPR: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invoiceService: InvoiceService,
    private authenticationInfoService: AuthenticationInfoService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.invoiceViewType = this.route.snapshot.paramMap.get('invoiceViewType');
    this.route.data.pipe(takeUntil(this.unsubscribe$)).subscribe((data) => {
      this.invoice = data.invoice;
      this.attachments = this.invoice.Attachments;

      this.hidePDFViewerButtonForPPR = this.invoice.IsPPR;

      this.canDeleteAttachment =
        this.invoice.CurrentAssignmentGroupId &&
        (this.authenticationInfoService.authentificatedUser.GroupsMembership.AllGroups.includes(
          this.invoice.CurrentAssignmentGroupId
        ) ||
          this.authenticationInfoService.authentificatedUser.GroupsMembership.ControlledOperationalGroups.includes(
            this.invoice.CurrentAssignmentGroupId
          ));

      this.CanManageInvoiceAGI = this.invoice.CanManageInvoiceAGI;
    });
  }

  goBackToList() {
    if (_.isNil(this.invoiceViewType)) {
      this.router.navigate(['/invoices', InvoiceViewType.SEARCH]);
      return;
    }

    this.router.navigate(['/invoices', this.invoiceViewType]);
  }

  postActionCallBack = (actionType: InvoiceActionType, context?: any): void => {
    switch (actionType) {
      case InvoiceActionType.ATTACH:
        this.invoiceService
          .getInvoiceById(this.invoice.Id)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((invoice: InvoiceDetailModel) => {
            this.invoice = invoice;
            this.attachments = invoice.Attachments;
          });
        return;
      case InvoiceActionType.UPDATEINVOICE:
        this.goBackToList();
      default:
        this.goBackToList();
        return;
    }
  };

  firstViewerDocumentSelector = (attachmentType: AttachmentType): boolean => {
    return attachmentType === AttachmentType.INVOICE;
  };

  secondViewerDocumentSelector = (attachmentType: AttachmentType): boolean => {
    return attachmentType !== AttachmentType.INVOICE;
  };

  fileName = (): string => {
    const regex_symbols = /[!$%^&*()_+|~=`{}\\\[\]:\/;<>?,.@#]/g;
    const date = moment(this.invoice.IssuedAt).format('YYYYMMDD');
    const primaryReference =
      !_.isNil(this.invoice.PrimaryReference) &&
      this.invoice.PrimaryReference.length
        ? `${this.invoice.PrimaryReference.replace(regex_symbols, '')}_`
        : '';
    const supplierCompanyName =
      !_.isNil(this.invoice.SupplierCompanyName) &&
      this.invoice.SupplierCompanyName.length
        ? `${this.invoice.SupplierCompanyName.replace(regex_symbols, '')}_`
        : '';
    const invoiceNumber = this.invoice.Number.replace(regex_symbols, '');
    return `${primaryReference}${supplierCompanyName}${invoiceNumber}_${date}.pdf`;
  };

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
  }

  get isEmptyEvaluaCompanyName(): boolean {
    const control: string = this.invoice.SupplierEvaluaCompanyName;
    const isEmpty = _.isNil(control)
      ? true
      : (control || '').trim().length === 0;
    return isEmpty && !this.CanManageInvoiceAGI;
  }

  get CanProcessInvoice(): boolean {
    return this.invoice.CanProcessInvoice;
  }

  get CanWaitInvoice(): boolean {
    this.setWaitingInvoice();
    return this.isWaitingInvoice;
  }

  setWaitingInvoice(): void {
    let result: boolean = false;
    if (this.invoice.IsPPR) {
      if (this.invoice.IsForPPRDirectInvoiceBDC) {
        result =
          this.invoice.Expenses.filter((c) => _.isNil(c.FamilyCode)).length > 0;
      }
    }
    result = result || this.isEmptyEvaluaCompanyName;
    this.isWaitingInvoice = result;
  }
}
